import { DnDNodeProps, ItemDate, WARNING_DAYS } from '@centerpiece/components';
import {
  Coordination,
  Event,
  Stage,
  Grouping,
  Item,
  Project,
  Task,
  NodeItem,
  PROJECTSTATES,
  ITEMSTATUSES,
  Message,
  Appointment,
  Response,
  MESSAGESTATUS,
} from 'store/api/models';
import { getCalendarTypeFromDate } from './status.utils';
import moment from 'moment';
import { useEffect } from 'react';

interface DndNodeRecursion {
  nodes: DnDNodeProps[];
  events: any[];
}

interface DndNodeTree {
  root: DnDNodeProps;
  events: any[];
}

function childrenWithDueDate(children: DnDNodeProps[]) {
  let childrenWDueDate: DnDNodeProps[] = [];
  let numChildren: number = 0;
  let childrenPastDue: number = 0;
  let childrenAtRisk: number = 0;
  let statusCount: { [key: string]: number } = {};
  children?.forEach((child: any) => {
    numChildren += child?.props?.numChildren;
    childrenPastDue += child?.props?.childrenPastDue;
    childrenAtRisk += child?.props?.childrenAtRisk;
    if (child?.data?.status_r?.name !== 'Complete' && child?.data?.due_date) {
      childrenWDueDate.push(child);
    }
    if (statusCount[child?.data?.status_r?.name]) {
      statusCount[child?.data?.status_r?.name] += 1;
    } else {
      statusCount[child?.data?.status_r?.name] = 1;
    }
  });
  childrenWDueDate.sort((a, b) =>
    moment(a.data?.due_date).diff(b.data?.due_date)
  );
  numChildren += children.length;
  const currentChildrenPastDue = childrenWDueDate?.filter(
    (item: any) =>
      item?.data?.due_date &&
      moment(item?.data?.due_date).diff(moment.now(), 'days') < 0
  )?.length;
  childrenPastDue += currentChildrenPastDue;
  childrenAtRisk +=
    childrenWDueDate?.filter(
      (item: any) =>
        item?.data?.due_date &&
        moment(item?.data?.due_date).diff(moment.now(), 'days') <= WARNING_DAYS
    )?.length - currentChildrenPastDue;
  return [
    numChildren,
    childrenWDueDate,
    childrenPastDue,
    childrenAtRisk,
    statusCount,
  ];
}

const normalizeProjectData = (project?: Project) => {
  const data = {
    orphaned: [] as Item[],
    stage: {} as { [key: number]: Stage },
    grouping: {} as { [key: number]: Grouping },
    item: {} as { [key: number]: NodeItem },
    task: {} as { [key: number]: Task },
    coordination: {} as { [key: number]: Coordination },
    event: {} as { [key: number]: Event },
  };

  const addRecursiveParent = (parent: number | null, item: number) => {
    if (parent) {
      data.item[parent] = {
        ...(data.item[parent] || {}),
        items: [
          ...(data.item[parent]?.items || []).filter(i => i.id !== item),
          {
            ...data.item[item],
            parentId: parent,
            parentNodeType: data.item[parent]?.nodeType,
            masked: data.item[parent]?.masked || data.item[parent]?.masked,
          },
        ],
      };
      if (data.item[parent].item) {
        addRecursiveParent(data.item[parent].item, parent);
      }
    }
  };
  project?.contents?.forEach((c: any) => {
    if (c.item) {
      const messages = project.msgs[c.item];
      const comments = project.cmts[c.item];
      let icons = [];
      // if (c.task || c.item?.form?.tabs?.info?.visible || true) {
      //   //always has info
      //   icons.push('task');
      // }
      // if (c.coordination || c.item?.form?.tabs?.message?.visible) {
      if (c.messages && Array.isArray(c.messages) && c.messages?.length > 0) {
        icons.push('coordination');
      }
      if (
        c.event ||
        c.item?.form?.tabs?.schedule?.visible ||
        c.item?.form?.tabs?.event?.visible
      ) {
        icons.push('event');
      }
      if (icons.length === 0) {
        //always has info
        icons.push('task');
      }
      const dates = getItemDates(c);
      data.item[c.item.id] = {
        ...c.item,
        messages: c.messages,
        responses: c.responses,
        appointments: c.appointments,
        task: c.task,
        coordination: c.coordination,
        event: c.event,
        grouping: c.grouping,
        stage: c.stage,
        status: c.status,
        relationships: c.relationships,
        items: data.item[c.item.id]?.items,
        tags: c.tags,
        nodeType: c.stage ? 'stage' : c.grouping ? 'grouping' : 'item',
        title: c.task?.title || c.grouping?.title || c.stage?.stage_r?.name,
        icons: icons,
        dates: dates,
        complete:
          project?.state === PROJECTSTATES.COMPLETE ||
          !!c.stage?.outcome ||
          c.status?.id === ITEMSTATUSES.COMPLETE,
        masked: project?.state === PROJECTSTATES.COMPLETE,
      };
      addRecursiveParent(c.parentitem?.id, c.item?.id);
    }
  });
  project?.msgs.forEach(m => {
    if (data.item[m.item.id]) {
      data.item[m.item.id]['msgs'] =
        (data.item[m.item.id]?.msgs || 0) + m.count;
      addRecursiveParent(data.item[m.item.id]?.item, m.item.id);
    }
  });
  project?.cmts.forEach(c => {
    if (data.item[c.item.id]) {
      data.item[c.item.id]['cmts'] =
        (data.item[c.item.id]?.cmts || 0) + c.count;
      addRecursiveParent(data.item[c.item.id]?.item, c.item.id);
    }
  });
  return {
    items: [
      ...Object.values(data.item).filter(
        (item: NodeItem) =>
          (!item.item || (item.id && !data.item[item.item]?.id)) &&
          ['event', 'coordination', 'task', 'item'].includes(item.nodeType)
      ),
    ].sort((a, b) => a.sequence - b.sequence),
    groupings: [
      ...Object.values(data.item).filter(
        (item: NodeItem) =>
          (!item.item || (item.id && !data.item[item.item]?.id)) &&
          'grouping' === item.nodeType
      ),
    ].sort((a, b) => a.sequence - b.sequence),
    stages: [
      ...Object.values(data.item).filter(
        (item: NodeItem) => !item.item && 'stage' === item.nodeType
      ),
    ].sort((a, b) => a.sequence - b.sequence),
  };
};

export const getOptimizedProjectTree = ({
  project: oProject,
  setActiveNode,
  activeNode,
  hash,
}: {
  project?: Project;
  setActiveNode?: Function;
  activeNode?: DnDNodeProps;
  hash?: string;
}): DndNodeTree => {
  let nodes: DnDNodeProps[] = [];
  let calendarEvents: any[] = [];
  if (oProject) {
    const project = normalizeProjectData(oProject);
    const { nodes: orphanedChildren, events: orphanedEvents } =
      createOptimizedDnDNode({
        collection: { items: project.items },
        setActiveNode,
        activeNode,
        hash,
      }); //orphaned
    const [
      numChildren,
      childrenWDueDate,
      childrenPastDue,
      childrenAtRisk,
      statusCount,
    ] = childrenWithDueDate(orphanedChildren);
    calendarEvents.push(...orphanedEvents);
    if (orphanedChildren && orphanedChildren.length > 0) {
      nodes.push({
        id: 0,
        nodeType: 'grouping',
        parentId: null,
        parentNodeType: 'project',
        props: {
          numChildren,
          childrenWDueDate,
          childrenPastDue,
          childrenAtRisk,
          statusCount,
        },
        data: { grouping: { title: 'Items to be defined' } },
        startExpanded: true,
        children: orphanedChildren,
      } as unknown as DnDNodeProps);
    }
    project.groupings?.forEach(grouping => {
      const { nodes: children, events } = createOptimizedDnDNode({
        collection: grouping,
        setActiveNode,
        activeNode,
        hash,
      });
      const [
        numChildren,
        childrenWDueDate,
        childrenPastDue,
        childrenAtRisk,
        statusCount,
      ] = childrenWithDueDate(children);
      calendarEvents.push(...events);
      nodes.push({
        id: grouping.id,
        nodeType: 'grouping',
        parentId: null,
        parentNodeType: 'project',
        props: {
          numChildren,
          childrenWDueDate,
          childrenPastDue,
          childrenAtRisk,
          statusCount,
        },
        data: grouping,
        sequence: grouping.sequence,
        startExpanded:
          grouping.status?.name === 'In Progress' ||
          !!children.find(c => c.startExpanded),
        children: children,
      } as unknown as DnDNodeProps);
    });
    let activeStageFound = false;
    project.stages?.forEach(stage => {
      let currentActiveStage;
      if (!stage.stage?.outcome && !activeStageFound) {
        activeStageFound = true;
        currentActiveStage = true;
      }
      const { nodes: children, events } = createOptimizedDnDNode({
        collection: stage,
        setActiveNode,
        activeNode,
        hash,
      });
      const [
        numChildren,
        childrenWDueDate,
        childrenPastDue,
        childrenAtRisk,
        statusCount,
      ] = childrenWithDueDate(children);
      calendarEvents.push(...events);
      const node = {
        id: stage.id,
        nodeType: 'stage',
        parentId: null,
        parentNodeType: 'project',
        props: {
          numChildren,
          childrenWDueDate,
          childrenPastDue,
          childrenAtRisk,
          statusCount,
        },
        data: stage,
        sequence: stage.sequence,
        startExpanded: currentActiveStage,
        children: children,
      } as unknown as DnDNodeProps;
      nodes.push(node);
      calendarEvents.push({
        id: `${stage.id}`,
        editable: false,
        title: stage?.stage?.stage_r?.name,
        start: stage.stage?.due_date,
        end: moment(stage.stage?.due_date).add(1, 'days').format('YYYY-MM-DD'),
        extendedProps: {
          icons: ['stage'],
          node: node,
          item_type: 'Stage',
          completed: stage.stage?.outcome,
          type: stage.stage?.outcome
            ? 'default'
            : getCalendarTypeFromDate(stage.stage?.due_date),
        },
      });
    });

    // return ({
    //   id: project.id,
    //   props: {},
    //   data: project,
    //   nodeType: 'project',
    //   parentId: 0,
    //   parentNodeType: 'index',
    //   children: nodes,
    //   sequence: 0,
    // })
    return {
      root: {
        id: oProject.id,
        props: {},
        data: project,
        nodeType: 'project',
        parentId: 0,
        parentNodeType: 'index',
        children: nodes,
        sequence: 0,
      },
      events: calendarEvents,
    };
  } else {
    return {
      root: {
        id: 0,
        props: {},
        data: {},
        nodeType: 'project',
        parentId: 0,
        parentNodeType: 'index',
        children: [],
        sequence: 0,
      },
      events: [],
    };
  }
};

export const createOptimizedDnDNode = ({
  collection,
  setActiveNode,
  activeNode,
  hash,
}: {
  collection: { complete?: boolean; items: NodeItem[] };
  setActiveNode?: Function;
  activeNode?: DnDNodeProps;
  hash?: string;
}): DndNodeRecursion => {
  let nodes: DnDNodeProps[] = [];
  let calendarEvents: any[] = [];
  const hashMatch = hash?.match(/(\w)=(\d+)/);
  collection?.items?.forEach((item: NodeItem, index) => {
    const { nodes: children, events } = createOptimizedDnDNode({
      collection: {
        complete: item.complete || item.status?.id === ITEMSTATUSES.COMPLETE,
        items: item.items,
      },
      setActiveNode,
      activeNode,
      hash,
    }); //recursive
    const [
      numChildren,
      childrenWDueDate,
      childrenPastDue,
      childrenAtRisk,
      statusCount,
    ] = childrenWithDueDate(children);
    calendarEvents.push(...events);
    const node: DnDNodeProps = {
      ...item,
      props: {
        numChildren,
        childrenWDueDate,
        childrenPastDue,
        childrenAtRisk,
        statusCount,
        msgs: item.msgs,
        cmts: item.cmts,
      },
      data: {
        ...item,
        complete: item.complete,
        masked: item.masked,
        status_r: item.status,
      },
      children: children,
      startExpanded: !!children.find(c => c.startExpanded),
    } as unknown as DnDNodeProps;
    if (
      (activeNode?.nodeType === item.nodeType && activeNode.id === item.id) ||
      (hashMatch?.length === 3 &&
        hashMatch[1] === item.nodeType[0] &&
        Number(hashMatch[2]) === item.id)
    ) {
      node.startExpanded = true;
      setActiveNode && setActiveNode(node); //refresh active node
    } else {
      node.startExpanded = node.startExpanded || item?.status?.id === 2;
    }
    nodes.push(node);

    if (item.nodeType !== 'grouping') {
      calendarEvents.push(...calendarEventsForItem(item, node));
    }
  });
  return {
    nodes: [...nodes].sort((a, b) => a.sequence - b.sequence),
    events: calendarEvents,
  };
};

export const incompleteChild = (children?: NodeItem[]): boolean => {
  if (children && Array.isArray(children)) {
    for (var child of children) {
      if (
        (!child.grouping && !child.complete) ||
        incompleteChild(child?.items)
      ) {
        return true;
      }
    }
  }
  return false;
};

export const calendarEventsForItem = (
  item: NodeItem,
  node: DnDNodeProps
): any[] => {
  let calendarEvents: any[] = [];
  item?.dates?.forEach(date =>
    calendarEvents.push({
      //task event
      id: `${item.id}`,
      editable: false,
      title: item?.task?.title,
      start: date.start || date.end || null,
      end:
        date.start && date.end
          ? moment(date.end).add(1, 'days').format('YYYY-MM-DD')
          : date.start && !date.end
          ? moment(date.start).add(1, 'days').format('YYYY-MM-DD')
          : date.end || null,
      extendedProps: {
        node: { ...node, icons: [date.icon || date.type] },
        icons: [date.icon || date.type],
        completed: item?.status?.name === 'Completed',
        type:
          item?.status?.name === 'Completed'
            ? 'default'
            : getCalendarTypeFromDate(date.end || date.start),
      },
    })
  );
  return calendarEvents;
};

// 3 different date types
// task due date, message response due date, and event date.
// propose that the next date takes precedent with the others trailing.
// event date > task due date > message response due date
export const getItemDates = (item?: Item): ItemDate[] => {
  let dates: ItemDate[] = [];
  if (item) {
    const task = item?.task_r || (item as any).task;
    const messages = item?.messages;
    const appointments = item?.appointments;

    if (task?.start_date || task?.due_date) {
      dates.push({
        sequence: 1,
        type:
          item?.form?.tabs?.schedule?.visible ||
          item?.form?.tabs?.event?.visible ||
          (item as any)?.item?.form?.tabs?.schedule?.visible ||
          (item as any)?.item?.form?.tabs?.event?.visible
            ? 'schedule'
            : 'task',
        append:
          item?.form?.tabs?.schedule?.visible ||
          item?.form?.tabs?.event?.visible ||
          (item as any)?.item?.form?.tabs?.schedule?.visible ||
          (item as any)?.item?.form?.tabs?.event?.visible
            ? 'Scheduling Due'
            : 'Item Start/Due Date(s)',
        start: task?.start_date ? new Date(task?.start_date) : null,
        end: task?.due_date ? new Date(task?.due_date) : null,
        disabled: false,
        onDateChange: (newValue: any) =>
          ({
            id: task?.id,
            // project: Number(id),
            due_date: newValue.end || null,
            start_date: newValue.start || null,
          } as Task),
      });
    }
    messages?.forEach((m: Message) => {
      if (m?.due_date && m.status !== MESSAGESTATUS.Replied) {
        dates.push({
          sequence: 1,
          type: 'resp_due',
          start: null,
          append: 'Message Response Due',
          end: new Date(m?.due_date),
          disabled: true,
        });
      }
    });
    let event = false;
    appointments?.forEach((a: Appointment) => {
      if (a?.start_time || a?.end_time) {
        event = true;
        dates.push({
          sequence: 1,
          type: 'event',
          append: 'Event Date(s)',
          start: a?.start_time ? new Date(a?.start_time) : null,
          end: a?.end_time ? new Date(a?.end_time) : null,
          disabled: true,
        });
      }
    });
    //Enforce hierarchy
    if (event) {
      dates = dates.filter(d => d.type !== 'schedule' && d.type != 'resp_due');
    }
    dates.sort((a, b) => moment(a.end).diff(b.end));
  }
  return dates;
};

export function waitForElm(selector: string) {
  return new Promise(resolve => {
    if (document.getElementById(selector)) {
      return resolve(document.getElementById(selector));
    }

    const observer = new MutationObserver(mutations => {
      if (document.getElementById(selector)) {
        resolve(document.getElementById(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

// eslint-disable-next-line
export function waitForAnimation(selector: string) {
  const el = document.getElementById(selector);
  if (el) {
    return new Promise(resolve => {
      const elemFunc = () => {
        resolve(document.getElementById(selector));
        el.removeEventListener('animationend', elemFunc);
      };
      el.addEventListener('animationend', elemFunc);
    });
  }
}

export function waitForInView(selector: string) {
  const el = document.getElementById(selector);
  if (el) {
    return new Promise(resolve => {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          resolve(document.getElementById(selector));
          observer.disconnect();
        }
      });

      observer.observe(el);
    });
  }
}
